import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Pen, Upload, Trash2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SignaturePad = ({ onSave, currentSignature, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (dialogOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [dialogOpen]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const saveSignature = async () => {
    if (!hasDrawn) {
      toast.error('Por favor, dibuja una firma primero');
      return;
    }

    const canvas = canvasRef.current;
    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('file', blob, 'signature.png');

      try {
        const response = await axios.post(`${API_URL}/courses/upload/signature`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        onSave(response.data.url);
        toast.success('Firma guardada');
        setDialogOpen(false);
      } catch (error) {
        toast.error('Error al guardar la firma');
      }
    }, 'image/png');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/courses/upload/signature`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSave(response.data.url);
      toast.success('Firma subida');
      setDialogOpen(false);
    } catch (error) {
      toast.error('Error al subir la firma');
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Firma del Director/a</label>
      
      {currentSignature ? (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
          <img 
            src={`${API_URL.replace('/api', '')}${currentSignature}`} 
            alt="Firma" 
            className="h-12 object-contain"
          />
          <div className="flex-1">
            <span className="text-sm text-green-600 flex items-center gap-1">
              <Check className="w-4 h-4" /> Firma guardada
            </span>
          </div>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={() => onClear()}
            className="text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" className="w-full">
              <Pen className="w-4 h-4 mr-2" />
              Añadir firma del director
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Firma del Director/a de Formación</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Canvas para dibujar */}
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-2">
                <p className="text-xs text-slate-500 mb-2 text-center">Dibuja tu firma aquí</p>
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={150}
                  className="border border-slate-200 rounded cursor-crosshair w-full"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <div className="flex gap-2 mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>
                    <Trash2 className="w-4 h-4 mr-1" /> Limpiar
                  </Button>
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={saveSignature}
                    className="bg-[#004E89] hover:bg-[#003B66]"
                  >
                    Guardar firma dibujada
                  </Button>
                </div>
              </div>

              {/* Separador */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-xs text-slate-400">o</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              {/* Subir imagen */}
              <div>
                <input
                  type="file"
                  id="signature-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <label htmlFor="signature-upload">
                  <Button type="button" variant="outline" className="w-full" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir imagen de firma
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-slate-400 mt-1 text-center">
                  PNG, JPG o GIF (fondo transparente recomendado)
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SignaturePad;
